import { z } from 'zod'

import { isoDateTime } from '../lib/date'
import type { AppointmentType } from './appointment-type'
import { clinicIdSchema } from './clinic'

export const appointmentActivityEventSchema = z.enum([
  'imported_from_digitail',
  'token_created',
  'booked_by_staff',
  'booked_by_member',
  'used_book_on_day_slot',
  // Any staff edit (status, time, clinic, and/or assignee) in a single save. The
  // specific fields that changed are in `metadata`.
  'staff_updated_appointment',
  'rescheduled_by_member',
  'cancelled_by_staff',
  'cancelled_by_member',
  'no_show_fee_charged',
  'no_show_fee_failed',
])
export type AppointmentActivityEvent = z.infer<typeof appointmentActivityEventSchema>

export const appointmentActivityActorTypeSchema = z.enum(['staff', 'member'])
export type AppointmentActivityActorType = z.infer<typeof appointmentActivityActorTypeSchema>

/**
 * Before/after values for one changed field. Values are stored raw and resolved to display strings client-side: status/clinic/type/staff are ids, and start/end times are ISO timestamps. `from` and `to` are nullable for fields that can be unset.
 */
export const appointmentActivityChangeSchema = z.object({
  from: z.string().nullable(),
  to: z.string().nullable(),
})
export type AppointmentActivityChange = z.infer<typeof appointmentActivityChangeSchema>

const appointmentActivityBaseSchema = z.object({
  /** Present for staff/member events; absent for system events such as imports. */
  actor: z
    .object({
      type: appointmentActivityActorTypeSchema,
      /** UUID of the staff member or member performing the action. */
      id: z.uuid(),
      /** First name snapshotted at the time of the action. */
      firstName: z.string(),
      /** Last name snapshotted at the time of the action. */
      lastName: z.string(),
    })
    .optional(),
  /** ISO 8601 timestamp of when the action occurred. */
  timestamp: z.iso.datetime(),
})

const timeChangesSchema = z.strictObject({
  startTime: appointmentActivityChangeSchema.optional(),
  endTime: appointmentActivityChangeSchema.optional(),
})

/**
 * A single appointment activity entry. Actor names are snapshotted at the time of the action so they survive staff/member renames. Metadata is event-specific: field changes for staff updates and member reschedules, or outcome payloads for fee charges and protected-slot usage.
 */
export const appointmentActivityEntrySchema = z.discriminatedUnion('event', [
  z.object({
    ...appointmentActivityBaseSchema.shape,
    event: z.enum([
      'imported_from_digitail',
      'token_created',
      'booked_by_staff',
      'booked_by_member',
      'cancelled_by_staff',
      'cancelled_by_member',
    ]),
    metadata: z.never().optional(),
  }),
  z.object({
    ...appointmentActivityBaseSchema.shape,
    event: z.literal('staff_updated_appointment'),
    /** Changes to status, start/end time, clinic, assigned vet and appointment type. */
    metadata: z.strictObject({
      ...timeChangesSchema.shape,
      status: appointmentActivityChangeSchema.optional(),
      clinic: appointmentActivityChangeSchema.optional(),
      staff: appointmentActivityChangeSchema.optional(),
      appointmentType: appointmentActivityChangeSchema.optional(),
    }),
  }),
  z.object({
    ...appointmentActivityBaseSchema.shape,
    event: z.literal('rescheduled_by_member'),
    /** Changes to the appointment's start and end times. */
    metadata: timeChangesSchema,
  }),
  z.object({
    ...appointmentActivityBaseSchema.shape,
    event: z.enum(['no_show_fee_charged', 'no_show_fee_failed']),
    metadata: z.strictObject({
      /** Outcome of an appointment penalty charge. `amount` is in the clinic region's currency minor units. */
      noShowFee: z.object({
        amount: z.number().int(),
        penaltyReason: z.enum(['no-show', 'late-change']).optional(),
        /** Invoice id when an invoice was created for the charge. */
        invoiceId: z.string().optional(),
        /** Stripe decline message or the reason for a failed charge attempt. */
        reason: z.string().optional(),
      }),
    }),
  }),
  z.object({
    ...appointmentActivityBaseSchema.shape,
    event: z.literal('used_book_on_day_slot'),
    metadata: z.strictObject({
      bookOnDay: z.object({
        action: z.enum(['booked', 'rescheduled']),
        appointmentTypeId: z.string(),
        lead_time_hours: z.number(),
        startTime: z.iso.datetime(),
        endTime: z.iso.datetime(),
        clinicId: z.string(),
        staffId: z.uuid(),
        templateName: z.string().optional(),
        windows: z.array(z.object({ startTime: z.iso.datetime(), endTime: z.iso.datetime() })),
      }),
    }),
  }),
])
export type AppointmentActivityEntry = z.infer<typeof appointmentActivityEntrySchema>

export const appointmentStatusSchema = z.enum([
  'pending',
  'confirmed',
  'checked-in',
  'in-progress',
  'completed',
  'cancelled',
  'no-show',
])

export type AppointmentStatus = z.infer<typeof appointmentStatusSchema>

export const MEMBER_APPOINTMENT_CHANGE_CUTOFF_MS = 24 * 60 * 60 * 1000

/**
 * Allowed status transitions for appointments. Each key maps to the set of
 * statuses it can transition to. `completed` and `cancelled` are terminal.
 *
 * Note: `in-progress` and `completed` are declared in the schema but not yet
 * reachable via any UI or backend action — they're included here for
 * completeness so the table is authoritative when those flows are built.
 */
export const APPOINTMENT_STATUS_TRANSITIONS: Record<
  AppointmentStatus,
  ReadonlySet<AppointmentStatus>
> = {
  pending: new Set(['confirmed', 'checked-in', 'no-show', 'cancelled']),
  confirmed: new Set(['checked-in', 'no-show', 'cancelled']),
  'checked-in': new Set(['completed', 'no-show', 'cancelled']),
  'no-show': new Set(['checked-in', 'confirmed', 'cancelled']),
  'in-progress': new Set(['completed']),
  completed: new Set([]),
  cancelled: new Set([]),
}

export const appointmentSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  bookingToken: z.string().nullable(),
  /** A current `AppointmentTypeId`, or a Digitail-era id such as 'allergy injection' on older appointments. */
  appointmentTypeId: z.string(),
  clinicId: clinicIdSchema.nullable(),
  staffId: z.string(),
  startTime: isoDateTime,
  endTime: isoDateTime,
  status: appointmentStatusSchema,
  /**
   * Staff-internal context about the visit. Not shown to the member, and not a
   * place for sensitive commentary about the member or pet — operational notes
   * only.
   *
   * The name is a bit of a misnomer: this is the staff-only field, not the
   * member's stated reason for booking (that's `userNotes`).
   */
  visitReason: z.string().nullable(),
  /**
   * Note from (or on behalf of) the member — what they want the vet to know
   * about this visit. Intended to be visible to the member.
   *
   * Surfaced in the UI as "Member notes".
   */
  userNotes: z.string().nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
  activityLog: z.array(appointmentActivityEntrySchema).nullable(),
})

export type Appointment = z.output<typeof appointmentSchema>

/** Returns the appointment's elapsed duration in minutes. */
export function calculateAppointmentDuration(appointment: {
  startTime: Date | string
  endTime: Date | string
}): number {
  return (
    (new Date(appointment.endTime).getTime() - new Date(appointment.startTime).getTime()) / 60_000
  )
}

/** Format an appointment type label with its duration, e.g. "Recheck (20 mins)". */
export function formatAppointmentTypeLabel(type: AppointmentType): string {
  return `${type.name} (${type.duration} mins)`
}

/** Returns true when member self-service rescheduling and cancellation are closed. */
export function isMemberAppointmentChangeWindowClosed(startTime: Date | string): boolean {
  return new Date(startTime).getTime() <= Date.now() + MEMBER_APPOINTMENT_CHANGE_CUTOFF_MS
}

export function canChargeNoShowFee(
  appointment: Pick<Appointment, 'status' | 'clinicId'> & { startTime: Date | string }
): boolean {
  return (
    Boolean(appointment.clinicId) &&
    APPOINTMENT_STATUS_TRANSITIONS[appointment.status].has('no-show') &&
    isMemberAppointmentChangeWindowClosed(appointment.startTime)
  )
}

/** Whether an appointment is eligible for a late-cancellation fee. */
export function canChargeLateChangeFee(
  appointment: Pick<Appointment, 'status' | 'clinicId'> & { startTime: Date | string }
): boolean {
  return (
    Boolean(appointment.clinicId) &&
    canCancelAppointment(appointment) &&
    // A no-show may already carry a penalty when it is subsequently cancelled.
    appointment.status !== 'no-show' &&
    isMemberAppointmentChangeWindowClosed(appointment.startTime)
  )
}

/** Whether an appointment can be edited (type, time, staff, etc.) by staff. Non-terminal statuses are editable. */
export function canEditAppointment(appointment: Pick<Appointment, 'status' | 'endTime'>): boolean {
  return (
    APPOINTMENT_STATUS_TRANSITIONS[appointment.status].size > 0 &&
    new Date(appointment.endTime) > new Date()
  )
}

/**
 * Whether an appointment can be cancelled by staff. Only statuses that can
 * transition to 'cancelled' — past appointments remain cancellable so staff can
 * tidy up bookings that were never checked in (e.g. a no-show that should have
 * been a cancellation).
 */
export function canCancelAppointment(appointment: Pick<Appointment, 'status'>): boolean {
  return APPOINTMENT_STATUS_TRANSITIONS[appointment.status].has('cancelled')
}
